'use client';
import { useState, useRef, useEffect } from 'react';
import Section from '@repo/ui/src/components/Section';
import LabeledValues, { LabeledValue } from './_LabeledValues';
import TextIconBoxes, { TextIconSingleBoxes, TextIconComboBox } from './_TextIconBoxes';
import { HeaderVariationProps } from '@repo/ui/src/types/headerVariation';
import ImageAndText, { ImageText } from './_ImageAndText';
import IconDescription, { IconAndDescriptionProps } from './_IconDescription';
import { ImageProps } from '@repo/ui/src/types/image';
import { classNames } from '@repo/ui/src/utils/classNames';

enum BackgroundStyle {
  Black = 'Black',
  TextureDark = 'TextureDark',
  White = 'White',
  Warm = 'Warm',
}

interface ProductTabs {
  name: string;
  labeledValues?: Array<LabeledValue>;
  leftProductImage: ImageProps;
  rightProductImage: ImageProps;
  textIconComboBox?: TextIconComboBox;
  textIconSingleBoxes?: Array<TextIconSingleBoxes>;
  rightProductImageText: ImageText;
  leftProductImageText: ImageText;
  iconAndDescription?: IconAndDescriptionProps;
}
interface Props {
  heading?: string;
  headerVariation: HeaderVariationProps;
  background: BackgroundStyle;
  tabAriaLabel?: string;
  products?: Array<ProductTabs>;
  _testID?: string;
  griddle?: boolean;
  tabs?: Array<ProductTabs>;
}

const GrillCapacityAndSpecs = ({
  heading,
  background = BackgroundStyle.Black,
  products,
  _testID,
  tabs,
}: Props) => {
  const [currentTab, setCurrentTab] = useState(0);
  const productTabs = products || tabs;

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: '0px', width: '0px' });

  useEffect(() => {
    const tab = tabRefs.current[currentTab];
    if (tab) {
      setIndicatorStyle({
        left: `${tab.offsetLeft}px`,
        width: `${tab.offsetWidth}px`,
      });
    }
  }, [currentTab]);

  if (!productTabs?.length) return null;

  const selectedProduct = productTabs[currentTab];
  const darkMode =
    background === BackgroundStyle.Black || background === BackgroundStyle.TextureDark;

  const tabsVisible = productTabs.length > 1;

  return (
    <Section
      black={background === BackgroundStyle.Black}
      white={background === BackgroundStyle.White}
      dark={background === BackgroundStyle.TextureDark}
      texture={background === BackgroundStyle.TextureDark}
      largePadding
      gutterX
      gutterY
      className="flex flex-col"
    >
      {heading && (
        <h2
          className={classNames(
            'tra-text-web-heading-2 self-center',
            darkMode ? 'text-neutral-100' : 'text-neutral-500',
            !tabsVisible && 'pb-20 mb-0',
          )}
        >
          {heading}
        </h2>
      )}
      {tabsVisible && (
        <div className="relative flex items-center justify-center pt-1 pb-5 md:pb-10">
          <ul className="relative flex list-none p-0 m-0" role="tablist">
            {productTabs.map(({ name }, index) => (
              <li key={name} className="list-none">
                <button
                  id={`product-tab-${index}`}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  onClick={() => setCurrentTab(index)}
                  className={classNames(
                    'py-2 mx-2 transition-colors tra-text-web-sub-2 hover:text-brand-300',
                    currentTab === index && 'text-brand-300',
                    currentTab !== index && (darkMode ? '!text-brand-50' : 'text-neutral-600'),
                  )}
                  role="tab"
                  aria-controls={`product-panel-${index}`}
                  aria-selected={currentTab === index ? 'true' : 'false'}
                >
                  {name}
                </button>
              </li>
            ))}
            <div
              className="absolute bottom-0 h-1 bg-brand-300 transition-all duration-300"
              style={indicatorStyle}
            />
          </ul>
        </div>
      )}
      <div className="grid md:max-w-[1200px] m-auto">
        {productTabs.map(
          (
            { leftProductImage, rightProductImage, leftProductImageText, rightProductImageText },
            i,
          ) => (
            <div
              key={i}
              id={`product-panel-${i}`}
              role="tabpanel"
              aria-labelledby={`product-tab-${i}`}
              hidden={i !== currentTab}
              className={classNames(
                'flex grid-in-a transition-opacity duration-300 justify-evenly gap-5',
                i === currentTab ? 'opacity-100' : 'opacity-0',
              )}
            >
              <ImageAndText image={leftProductImage} text={leftProductImageText} />
              <ImageAndText image={rightProductImage} text={rightProductImageText} />
            </div>
          ),
        )}
      </div>
      <div className="flex flex-col pt-20 gap-10">
        {selectedProduct?.labeledValues && (
          <>
            <LabeledValues labeledValues={selectedProduct.labeledValues} darkMode={darkMode} />
            {selectedProduct.textIconComboBox && selectedProduct.textIconSingleBoxes && (
              <TextIconBoxes
                textIconSingleBoxes={selectedProduct.textIconSingleBoxes}
                textIconComboBox={selectedProduct.textIconComboBox}
                darkMode={darkMode}
              />
            )}
          </>
        )}
        {selectedProduct.iconAndDescription && (
          <IconDescription {...selectedProduct.iconAndDescription} />
        )}
      </div>
    </Section>
  );
};

export default GrillCapacityAndSpecs;
